import React, { useState } from 'react';
import { IoTimeOutline, IoPersonOutline } from 'react-icons/io5';
import { deleteMeetingApplication, acceptMeetingApplication } from '../../api/MeetingApplicationApi';
import useLoadingStore from '../../store/loadingStore';
import { AlertModal } from '../layouts/AlertModal';
import { ConfirmModal } from '../layouts/ConfirmModal';

const MeetingMeetingApplicationContainer = ({ application, meetingId, onUpdate }) => {
    const { setIsLoading } = useLoadingStore();

    // 모달 상태 관리
    const [alertState, setAlertState] = useState({ isVisible: false, message: '', callback: null });
    const [confirmState, setConfirmState] = useState({ isVisible: false, title: '', message: '', onConfirm: () => {} });

    // 헬퍼 함수
    const showConfirm = (title, message, onConfirmAction) => {
        setConfirmState({
            isVisible: true,
            title,
            message,
            onConfirm: () => {
                onConfirmAction();
                setConfirmState({ ...confirmState, isVisible: false });
            }
        });
    };

    const getErrorMessage = (error) => {
        if (error.response && error.response.data && error.response.data.message) {
            return error.response.data.message;
        }
        return '오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const hours = date.getHours();
        const minutes = date.getMinutes();

        return `${year}.${month.toString().padStart(2, '0')}.${day.toString().padStart(2, '0')} ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };

    const handleApprove = async () => {
        showConfirm('신청서 승인', `${application.memberName}님의 신청서를 승인하시겠습니까?`, async () => {
            try {
                setIsLoading(true);
                const acceptRequest = {
                    meetingId: Number(meetingId),
                    memberId: application.memberId
                };
                await acceptMeetingApplication(application.id, acceptRequest);
                setAlertState({
                    isVisible: true,
                    message: `${application.memberName}님의 신청서를 승인했습니다.`,
                    callback: onUpdate
                });
            } catch (error) {
                console.error('신청서 승인 실패:', error);
                setAlertState({ isVisible: true, message: getErrorMessage(error) });
            } finally {
                setIsLoading(false);
            }
        });
    };

    const handleReject = async () => {
        showConfirm('신청서 거절', `${application.memberName}님의 신청서를 거절하시겠습니까?`, async () => {
            try {
                setIsLoading(true);
                await deleteMeetingApplication(application.id);
                setAlertState({
                    isVisible: true,
                    message: `${application.memberName}님의 신청서를 거절했습니다.`,
                    callback: onUpdate
                });
            } catch (error) {
                console.error('신청서 거절 실패:', error);
                setAlertState({ isVisible: true, message: getErrorMessage(error) });
            } finally {
                setIsLoading(false);
            }
        });
    };

    const isValidImageUrl = (url) => {
        return url && url !== 'null' && url !== null && url.trim() !== '';
    };

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3">
            <AlertModal
                isVisible={alertState.isVisible}
                message={alertState.message}
                callback={alertState.callback}
                onClose={() => setAlertState({ isVisible: false, message: '', callback: null })}
            />
            <ConfirmModal
                isVisible={confirmState.isVisible}
                title={confirmState.title}
                message={confirmState.message}
                onConfirm={confirmState.onConfirm}
                onCancel={() => setConfirmState({ ...confirmState, isVisible: false })}
            />
            <div className="flex items-start space-x-3">
                {/* 멤버 프로필 이미지 */}
                <div className="flex-shrink-0">
                    {isValidImageUrl(application.memberImageUrl) ? (
                        <img
                            src={application.memberImageUrl}
                            alt={application.memberName}
                            className="w-12 h-12 rounded-full object-cover"
                        />
                    ) : (
                        <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center">
                            <IoPersonOutline size={24} className="text-gray-600" />
                        </div>
                    )}
                </div>

                {/* 신청서 내용 */}
                <div className="flex-1">
                    <div className="flex items-center mb-1">
                        <h3 className="font-medium text-gray-900">
                            {application.memberName}
                        </h3>
                    </div>

                    <div className="flex items-center text-sm text-gray-500 mb-3">
                        <IoTimeOutline size={16} className="mr-1" />
                        <span>신청일: {formatDate(application.createdAt)}</span>
                    </div>

                    {/* 신청서 내용 */}
                    <div className="mb-3">
                        <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                            {application.content}
                        </p>
                    </div>

                    {/* 승인/거절 버튼 */}
                    <div className="flex space-x-2">
                        <button
                            onClick={handleApprove}
                            className="flex-1 bg-blue-500 text-white py-2 px-4 rounded-lg font-medium active:bg-blue-600 transition-colors"
                        >
                            승인
                        </button>
                        <button
                            onClick={handleReject}
                            className="flex-1 bg-red-500 text-white py-2 px-4 rounded-lg font-medium active:bg-red-600 transition-colors"
                        >
                            거절
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MeetingMeetingApplicationContainer;
